import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { Seo } from '@/layout/Seo'
import { HideTopNavigationLayout } from '@/layouts/HideTopNavigationLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { getShareLink, ShareLink } from '@/services/ContentShareService'
import { getProjectAbout, ProjectMeta } from '@/services/ProjectsService'
import { getContentForSharePage, SharePageContent } from '@/services/ProjectsService/ProjectsService'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { noPreRenderedStaticPaths } from '@/utils/nextUtils/staticPages'
import { getBasePath } from '@/utils/sitemap/base'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PerfLogsProvider } from '@/utils/usePerfLogs'
import { ContentSharedViewDisplayFields, OneToManyContentSharedView } from '@/views/ContentSharedView'

interface PageProps {
  id: string
  contentId: string
  locale: string
  shareLink: ShareLink
  displayFields: ContentSharedViewDisplayFields
  pageTitle: string | null
  projectMeta: ProjectMeta | null
}

// NOTE This page is for 1:many sharing, and is a very different experience than 1:1 sharing. See /watch/share for 1:1
export const OneToManyContentSharedPage: NextPageWithLayout<PageProps> = (props) => {
  const { shareLink, id, contentId, locale, displayFields, pageTitle, projectMeta } = props
  const path = `${paths.watch.index}/shared/${id}`
  const url = `${getBasePath(locale)}${path}`

  const metadata = shareLink.metadata
  const description = metadata.opengraph_description
  const title = pageTitle ?? metadata.opengraph_title
  const images = [
    {
      url: metadata.opengraph_image,
      width: 1200,
      alt: description,
    },
  ]

  return (
    <>
      <Seo
        canonical={url}
        description={description}
        openGraph={{ title, description, url, images }}
        path={path}
        title={title}
        nofollow
        noindex
      />
      <PerfLogsProvider>
        <ProjectMetaProvider project={projectMeta}>
          <MarketingVideoPopup />
          <OneToManyContentSharedView shareLink={shareLink} contentId={contentId} displayFields={displayFields} />
        </ProjectMetaProvider>
      </PerfLogsProvider>
    </>
  )
}

OneToManyContentSharedPage.getLayout = (page) => {
  return <HideTopNavigationLayout isDarkMode={true}>{page}</HideTopNavigationLayout>
}

export const getStaticPaths = noPreRenderedStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<PageProps>(
  { filePath: 'pages/[locale]/watch/shared/[id]/index.tsx' },
  async ({ locale, params, next404, federationClient, preview }) => {
    const id = getItemOrFirstEntry(params.id)
    if (!id) {
      return {
        redirect: {
          destination: paths.watch.index,
          permanent: false,
        },
      }
    }

    const shareLink = await getShareLink({ id })
    if (!shareLink) {
      return next404(Durations.ONE_MINUTE_IN_SECONDS)
    }

    const shareContent = await getContentForSharePage(shareLink.content_id, federationClient)
    if (!shareContent) {
      return next404(Durations.ONE_MINUTE_IN_SECONDS)
    }

    const [projectMeta, translations] = await Promise.all([
      getProjectAbout({ slug: shareContent.projectSlug }, federationClient, { locale, preview }),
      loadTranslations(locale, ['common', 'watch', 'guild']),
    ])

    const displayFields = getDisplayFields(shareContent)
    const pageTitle = getPageTitle(shareContent)

    return {
      props: {
        displayFields,
        pageTitle: pageTitle ?? null,
        shareLink,
        locale,
        id,
        contentId: shareLink.content_id,
        projectMeta,
        ...translations,
      },
      revalidate: Durations.THIRTY_MINUTES_IN_SECONDS,
    }
  },
)

function getDisplayFields(content: SharePageContent): ContentSharedViewDisplayFields {
  if (content.isEpisode) {
    const subTitle =
      typeof content.seasonNumber === 'number' && typeof content.episodeNumber === 'number'
        ? `S${content.seasonNumber}:E${content.episodeNumber} ${content.projectName}`
        : undefined

    return { title: content.contentName, subTitle }
  }
  return { title: content.contentName }
}

function getPageTitle(content: SharePageContent): string | undefined {
  if (!content.isEpisode) return undefined

  if (typeof content.seasonNumber !== 'number' || typeof content.episodeNumber !== 'number') {
    return undefined
  }

  return `Watch ${content.projectName} S${content.seasonNumber}E${content.episodeNumber} Free — Shared by an Angel Guild Member`
}

export default OneToManyContentSharedPage
